import { FinishBy } from "./guest/time";
import { ActionRecaptchaNotice } from "../../../../../shared/ui/RecaptchaNotice";
import { FlowFrame, requestStepFocus } from "../../components/PageShell";
import { useNow } from "../../lib/useNow";
import { usePageTitle } from "../../../../../shared/hooks/usePageTitle";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";
import type {
  BookingCancellationPolicyDisplay,
  PublicProfessionalProfile,
} from "../../features/profile/operations";
import type { FlowSelection } from "../../features/booking/useFlowState";
import { StepLeadBar, StepFoot, StepMeta } from "./StepChrome";
import { TermsAgreement } from "../../components/TermsAgreement";
import { PolicyCard } from "../../components/PolicyCard";
import { revealInScroller } from "../../lib/scrollerOf";
import type { TermsConsent } from "../../features/terms/useTermsConsent";

/**
 * What the screen is currently able to say about the booking.
 *
 * Five values, and none of them is decoration — each one answers "what was
 * booked and what was charged" differently, and getting that wrong is worse
 * than showing nothing. `sending` is NOT one of them: a send can be in flight
 * from `default` or from `failed`, so it is an orthogonal flag rather than a
 * sixth state.
 */
export type ReviewState =
  | "default"
  | "expired"
  | "taken"
  | "failed"
  | "offline"
  /** No live hold at all — pasted URL, refresh, or a lost anonymous identity. */
  | "nohold"
  /**
   * The request left the device and no answer came back. It MAY have committed
   * — and under booking-v1@1.4.0 that is now a question the client can ask,
   * because the attempt has a name. See `onCheckResult`.
   */
  | "uncertain"
  /** The professional changed payment settings mid-booking. */
  | "settings"
  /** This professional takes bookings only from signed-in accounts. */
  | "accountRequired"
  /** The server says the booking already exists. Show it; never retry. */
  | "alreadyBooked"
  /**
   * This professional cannot be booked this way (a payment-mode refusal).
   * Nothing was written and no retry can succeed, so none is offered. Shows
   * only the existing title until Product supplies the wording.
   */
  | "paymentUnavailable"
  /**
   * Backend refused the Terms acceptance as stale or missing (booking-v1
   * 1.13+). The Terms are being re-read and the box is unticked; nothing goes
   * until the person agrees to what is in force now. Not blocked: once they
   * tick, Confirm re-asks under the SAME idempotency key.
   */
  | "termsChanged"
  /**
   * A Terms refusal answered a CHECK after a lost answer: the first send may
   * have committed. The existing "may have gone through" notice stays, with
   * the Terms re-ask beneath it (audit WEB-TOS-03). No Check button: the
   * next Confirm, once the Terms are agreed, asks under the SAME key, which
   * replays a committed booking. With no live hold, Confirm is blocked and
   * the notice offers another time.
   */
  | "uncertainTerms";

interface Props {
  verifiedGuest?: boolean;
  /**
   * Activate the recovery check. Wired ONLY to the button in the uncertain
   * notice — never to an effect, a timer, a reconnect or a mount, because an
   * automatic replay is a request nobody asked for against a booking that may
   * already exist.
   */
  onCheckResult?: () => void;
  /** A check is in flight: the control is disabled and reads "Checking…". */
  checking?: boolean;
  /** The check ALSO lost its answer, which changes the body and nothing else. */
  checkAlsoLost?: boolean;
  slug: string;
  profile: PublicProfessionalProfile;
  selection: FlowSelection;
  /**
   * Name and email from step 3.
   *
   * Not read from `selection`: the flow keeps its state in the URL, and a
   * client's name and email have no business in a link that gets shared.
   */
  client: { name: string; email: string };
  state: ReviewState;
  /**
   * Seconds left on the hold, or null when nothing is being held.
   *
   * Read as a fact, not a hint: it decides whether the countdown renders and
   * whether the `failed` notice is allowed to say the time is still held. Pass
   * null the moment the hold is gone — a stale number here would print a
   * promise this screen cannot keep.
   */
  secondsLeft: number | null;
  /** Backend hold deadline; never reconstructed from seconds remaining. */
  holdExpiresAt?: string | null;
  /** A confirm is in flight. */
  sending: boolean;
  /**
   * rc.10: why the last press stopped before sending. "changed": the policy
   * shown now is new, and a fresh press is required. "unreadable": the
   * re-read failed twice, and pressing again is the retry.
   */
  policyNote?: "changed" | "unreadable" | null;
  /**
   * The professional's cancellation policy, AS THE SERVER STATES IT.
   *
   * `PublicProfessionalProfile.cancellationPolicyDisplay` from
   * `booking-v1@1.2.0`, passed straight through. A prop and never a constant:
   * a hardcoded "24 hours" becomes a lie the moment one professional sets 48 —
   * on the screen a client reads to decide whether to commit, and later quotes
   * back when they want to cancel.
   *
   * NULL MEANS FAIL CLOSED. The contract documents null as "the policy cannot
   * be represented safely" and instructs consumers not to substitute local
   * copy. It is the server saying it cannot state the terms — NOT that there
   * are none — so this screen then says nothing about terms at all. See the
   * render site.
   */
  cancellationPolicyDisplay: BookingCancellationPolicyDisplay | null;
  /**
   * LURA'S TERMS OF SERVICE (booking-v1 1.13+), read by the caller for the
   * page's language. REQUIRED, so no Review can render without deciding what
   * it shows about them. The primary action stays disabled until
   * `terms.decision()` is non-null: loading, unreadable, or published and
   * unticked all hold it off. The caller sends exactly that decision.
   */
  terms: TermsConsent;
  // wired in Stage 3 — commits the booking. Also serves the `failed` notice's
  // retry, because "try again" and "confirm" are the same call.
  onConfirm(): void;
  /**
   * GUEST VERIFICATION ONLY. A notice the guest checkout needs shown in this
   * column (the session could not be read on Continue). Absent on the legacy
   * path, which renders exactly as before.
   */
  notice?: ReactNode;
  /**
   * GUEST VERIFICATION ONLY. On that path the primary action continues to
   * the code step (it sends the code, it does not book): "Continue", busy
   * "Sending code…". Absent on the signed-in path.
   */
  primaryLabels?: { idle: string; busy: string };
}

/**
 * Mirrors StepTime's own copy. StepChrome is not this track's to extend.
 *
 * Clamped at zero: a hold that ticks a second past its expiry before the parent
 * notices would otherwise render "-1:-5" on the commit screen.
 */
function mmss(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  return `${minutes}:${String(safe % 60).padStart(2, "0")}`;
}

/**
 * `.b-notice` is a flex ROW — the icon sits beside the text. Everything textual
 * therefore has to go inside one flex item, which is what the kit's `BNotice`
 * does. Without it the title, body and action become three columns and the copy
 * renders one word per line on a phone.
 */

function addMinutes(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}

// Step 4 of 4 — the commit screen.
//
// The last screen before someone is on the hook for an appointment, and the
// only one where "something went wrong" would be a defect rather than a rough
// edge. Every failure below names what was booked and what was charged before
// it names what to do next.

function StepReview({
  slug,
  profile,
  selection,
  client,
  state,
  secondsLeft,
  holdExpiresAt = null,
  sending,
  policyNote = null,
  cancellationPolicyDisplay,
  terms,
  onConfirm,
  notice,
  primaryLabels,
  verifiedGuest = false,
  onCheckResult,
  checking = false,
  checkAlsoLost = false,
}: Props) {
  const { t, fmt, locale } = useTranslation();
  const deadlineMs = holdExpiresAt ? Date.parse(holdExpiresAt) : NaN;
  const deadlineNow = useNow(Number.isFinite(deadlineMs) ? deadlineMs : null);
  usePageTitle(t(verifiedGuest ? "guest.review.heading" : "flow.review.heading"));
  const headingId = useId();
  // The approval line and the agreement describe the button, so a Tab user
  // hears them with it (reviewer, rc.10).
  const actionNoteId = useId();
  /*
   * THE POLICY IS SHOWN, OR NOTHING IS SENT, ON BOTH PATHS (rc.10; booking-v1
   * says a null display fails closed, and Backend confirms "no policy" is not
   * a V1 state: every booking applies V1_24H). A null display, or a blank
   * headline or body, blocks the send with the existing notice. Before rc.10
   * only the guest was blocked; the signed-in path booked against a policy it
   * had not shown. The agreement is the sentence near the action
   * (Legal/Product, 2026-10-15), not a checkbox: the guest's "I agree to the
   * booking terms shown above." box is gone.
   */
  const policyShown = Boolean(cancellationPolicyDisplay?.headline.trim() && cancellationPolicyDisplay?.body.trim());
  const policyUnavailable = !policyShown;
  // Read through the router rather than off `window`. A global read during
  // render is not something the React Compiler can key a memo on, so a cached
  // `<Link to>` could outlive the query string it was built from.
  const { search } = useLocation();
  const { serviceId, startTime } = selection;
  const service = profile.services.find((candidate) => candidate.id === serviceId);
  const timeZone = profile.timezone;

  const expired = state === "expired";
  const taken = state === "taken";
  const failed = state === "failed";
  const paymentUnavailable = state === "paymentUnavailable";
  const offline = state === "offline";
  const nohold = state === "nohold";
  const uncertain = state === "uncertain";
  /**
   * WHERE FOCUS GOES WHEN CONFIRM DISAPPEARS.
   *
   * Entering `uncertain` REMOVES the Confirm button — deliberately, because it
   * must not be a second path to a send. But the person who pressed it was
   * focused on it, so its removal drops focus to `<body>`: measured by QE in
   * both Chromium and WebKit, on the most consequential screen in the flow, and
   * it is the same defect this codebase has already fixed twice on the auth
   * surface and guards against twice more in the comments below.
   *
   * The notice takes the focus instead, because the notice now owns the action.
   *
   * ONLY ON ENTRY. `checkAlsoLost` flips the body copy while the person is
   * reading it, and re-focusing then would yank them back to the top of a
   * notice they are already in — which is why this tracks the transition rather
   * than the state. The polite live region announces that change; focus does
   * not move for it.
   */
  const uncertainNotice = useRef<HTMLDivElement>(null);
  const wasUncertain = useRef(false);
  useEffect(() => {
    if (uncertain && !wasUncertain.current) uncertainNotice.current?.focus();
    wasUncertain.current = uncertain;
  }, [uncertain]);

  /**
   * WHEN A SEND UNMOUNTS THE CONTROL THAT STARTED IT.
   *
   * Pressing the retry inside the `failed` notice dispatches, the state moves
   * off `failed` on the very next render, and the WHOLE NOTICE unmounts —
   * taking the pressed button, and the focus, with it. Nothing was disabled;
   * the element simply stopped existing. So the `aria-disabled` treatment on
   * that control, correct as it is, could never have addressed this: review
   * pointed out that `failed` and `sending` cannot even coexist in one render.
   *
   * Confirm is the right destination. It is the same action, it is present in
   * every state but `uncertain`, and it announces itself busy — so a keyboard
   * visitor lands on a control that tells them what is happening instead of at
   * the top of the document.
   *
   * NARROW ON PURPOSE. It fires only when a send BEGINS and focus has already
   * fallen to `<body>` — the signature of exactly this unmount.
   *
   * IT IS NOT, HOWEVER, UNREACHABLE FROM A CONFIRM-INITIATED SEND, and an
   * earlier version of this comment said it was. QE measured the effect's view
   * of `document.activeElement` on a plain pointer click: Chromium focuses the
   * button, so the effect does not fire; WEBKIT DOES NOT focus a button on
   * click, so it does fire and moves focus to Confirm programmatically.
   *
   * Harmless, and measured rather than assumed: after a pointer click the
   * button reports `:focus-visible === false` in both engines, so no focus ring
   * appears for a mouse user. The behaviour is fine; the claim was not — and
   * this is the second comment on this control to overstate what it knew, which
   * is why the measurement is written down instead of the reassurance.
   */
  const confirmButton = useRef<HTMLButtonElement>(null);
  // A press that stopped (rc.10) shows its line above the button. "Has
  // changed": the NEW policy is put in front of the person, so the card is
  // brought into view (rc.11 F2, the Web Director: the wording must be on
  // screen when they read "Review it before continuing"). "Unreadable": the
  // button and its lines, where the bar unpins (v3.css, 34em). Focus is
  // never moved; instant under reduced motion.
  const policyCard = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Its top first: the most of the new wording on screen (Web Director).
    // No card rendered: the button and its lines, as for "unreadable".
    const card = policyNote === "changed" ? policyCard.current : null;
    if (card) revealInScroller(card, "start");
    else if (policyNote && confirmButton.current) revealInScroller(confirmButton.current);
  }, [policyNote]);
  const wasSending = useRef(false);
  useEffect(() => {
    if (sending && !wasSending.current && document.activeElement === document.body) {
      confirmButton.current?.focus();
    }
    wasSending.current = sending;
  }, [sending]);
  const settings = state === "settings";
  const accountRequired = state === "accountRequired";
  const alreadyBooked = state === "alreadyBooked";
  const termsChanged = state === "termsChanged";
  const uncertainTerms = state === "uncertainTerms";
  // Whether a hold is live is the countdown's own fact: null means none.
  const uncertainNoHold = uncertainTerms && secondsLeft === null;
  // NO DECISION, NO SEND. Separate from `blocked` because it is not a
  // notice-worthy state: the control right above the button says why.
  const termsUndecided = terms.decision() === null;

  // A link that went stale in a message can name a service that no longer
  // exists, and Book.tsx's guard rail checks only that `?service=` is PRESENT.
  // The screen then cannot describe what it would book — no price, no time, no
  // footer total — and a live Confirm under that is asking someone to commit to
  // something nobody has shown them.
  const incomplete = !service || !startTime;

  // BLOCKED means confirming cannot succeed right now, for a reason the client
  // has to act on first: the slot is no longer theirs (expired, taken), the
  // request cannot leave the device (offline), or the selection itself is gone.
  // `failed` is deliberately NOT here — the time is still held, so the retry is
  // the primary button itself.
  //
  // Every one of the added states is blocked because confirming from it cannot
  // succeed. `nohold` is the one that was MISSING, and its absence was the
  // whole defect: with no hold the screen matched none of the blocking
  // conditions, so Confirm rendered live and its handler returned immediately
  // on `hold.state.status !== "held"`. A button that looks operable, is not,
  // and says nothing when pressed.
  //
  // `uncertain`, `accountRequired` and `alreadyBooked` are blocked for a
  // stronger reason than "cannot succeed": pressing again could make things
  // WORSE. The first may already have committed.
  //
  // The old wording here said the mutation "is not documented as idempotent".
  // That was true of `booking-v1@1.2.0` and is FALSE at the pinned 1.4.0, which
  // documents `CreateBookingInput.idempotencyKey` and the replay semantics this
  // whole batch is built on. The reason Confirm stays blocked in `uncertain` is
  // not that a replay is undefined — it is that Confirm would mint a NEW
  // attempt. The recovery control resends the frozen one, which is exactly why
  // it is the only action offered there.
  const holdBlocked =
    expired || taken || offline || incomplete || nohold || uncertain || settings ||
    accountRequired || alreadyBooked || paymentUnavailable || uncertainNoHold;
  const blocked = holdBlocked || policyUnavailable;

  // The countdown follows the HOLD, not the state name. The design shows it
  // only on `default`, but `failed` tells the client in as many words that
  // their time is still held — hiding a live countdown underneath that sentence
  // makes the screen argue with itself.
  const heldLabel = secondsLeft !== null && !holdBlocked ? mmss(secondsLeft) : null;

  // `– end` needs a duration, and a review row with a start but no end invites
  // someone to plan around a length they guessed.
  const endTime = startTime && service ? addMinutes(startTime, service.durationMinutes) : null;

  // Top to bottom: what the client is committing to, in the order they chose
  // it. A null value renders as a label alone rather than an empty line.

  const timeLabel = startTime ? fmt.time(startTime, timeZone) : "";

  // Back to step 2 WITHOUT the dead slot. Both notices that link here do so
  // because the chosen time is gone; carrying `?time=` along would land the
  // client on the time list with that slot rendered as their selection and
  // Continue disabled, explaining nothing. Product's rule for expiry is to
  // preserve the details and clear only the time, and this is that rule applied
  // to the link rather than to the hook.
  const timeParams = new URLSearchParams(search);
  timeParams.delete("time");
  const backToTime = `/${slug}/book/time?${timeParams.toString()}`;

  return (
    <FlowFrame
      testId="step-review"
      className="b3-review"
      data={{ "review-state": state }}
      lead={<StepLeadBar backTo={`/${slug}/book/details${search}`} step={4} of={verifiedGuest ? 5 : 4} v3 />}
    >

      {/* Review can exceed a phone viewport. Give the inner scroll region a
          keyboard stop and the visible heading as its name, so the policy
          below the fold is reachable without a pointer. */}
      <div className="b-scroll" role="region" aria-labelledby={headingId} tabIndex={0}>
        <div className="b-stack g6">
          <h1 className="b-display" id={headingId} tabIndex={-1}>{t(verifiedGuest ? "guest.review.heading" : "flow.review.heading")}</h1>
          {t("flow.review.nothingCharged") ? <p className="b2-sub">{t("flow.review.nothingCharged")}</p> : null}
        </div>

        {notice}

        {/* Expired and taken both end with "your slot is gone", and the copy
            still has to differ: expired means nobody has it, taken means
            somebody does. One invites re-picking the same time, the other does
            not, so they never share a notice. */}
        {expired ? (
          <div className="b-notice t-warning" role="alert" data-testid="review-expired">
            <div className="b-notice-b">
              <b>{t("flow.review.expired.title")}</b>
              {t("flow.review.expired.body") ? <p className="b-p">{t("flow.review.expired.body", { time: timeLabel })}</p> : null}
              <div className="b-notice-a">
                <Link className="b-btn v-secondary s-sm" to={backToTime} onClick={() => requestStepFocus()}>
                  {t("flow.review.expired.action")}
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        {taken ? (
          <div className="b-notice t-danger" role="alert" data-testid="review-taken">
            <div className="b-notice-b">
              <b>{t("flow.review.taken.title", { time: timeLabel })}</b>
              <p className="b-p">{t("flow.review.taken.body")}</p>
              <div className="b-notice-a">
                <Link className="b-btn v-secondary s-sm" to={backToTime} onClick={() => requestStepFocus()}>
                  {t("flow.review.taken.action")}
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        {nohold ? (
          <div className="b-notice t-warning" role="alert" data-testid="review-nohold">
            <div className="b-notice-b">
              <b>{t("flow.review.nohold.title")}</b>
              {t("flow.review.nohold.body") ? <p className="b-p">{t("flow.review.nohold.body")}</p> : null}
              <div className="b-notice-a">
                <Link className="b-btn v-secondary s-sm" to={backToTime} onClick={() => requestStepFocus()}>
                  {t("flow.review.nohold.action")}
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        {/*
          THERE IS AN ACTION NOW, AND IT IS NOT A RETRY.

          It resends the SAME request under the SAME idempotency key, so the
          server answers with the original booking rather than making a second
          one. That distinction is the entire reason a button may appear here at
          all; a "try again" that rebuilt the request would be the duplicate
          this state exists to prevent.

          `role="status"` and not `role="alert"`: the second body arrives while
          the visitor is already reading this notice, and an assertive
          interruption for "still nothing" interrupts them to say nothing new.
          The notice keeps its place, the text is announced politely, and focus
          is left exactly where the person put it.
        */}
        {uncertain ? (
          <div
            className="b-notice t-warning"
            /*
             * FOCUS TARGET AND LIVE REGION ARE NOW DIFFERENT NODES.
             *
             * Both used to be this div, and review flagged the overlap: several
             * screen-reader and browser pairs announce a focused container's
             * contents, so a polite region that ALSO receives focus can be read
             * twice. Moving focus here is still strictly better than the
             * measured drop to `<body>`, so the split is a refinement rather
             * than a correction.
             *
             * The live region moved down to the body paragraph, which is the
             * only element whose text actually changes — `checkAlsoLost` swaps
             * it and nothing else — so the polite update still happens where it
             * means something.
             *
             * Programmatically focusable, not tab-reachable: this is a
             * destination for focus that has nowhere else to go, and putting it
             * in the tab order as well would add a stop nobody asked for.
             */
            tabIndex={-1}
            ref={uncertainNotice}
            data-testid="review-uncertain"
          >
            <div className="b-notice-b">
              <b>{t("flow.review.uncertain.title")}</b>
              <p
                className="b-p"
                role="status"
                aria-live="polite"
                data-testid="review-uncertain-body"
              >
                {checkAlsoLost
                  ? t("flow.review.uncertain.bodyAgain")
                  : t("flow.review.uncertain.body")}
              </p>
              <div className="b-notice-a">
                {/*
                  DISABLED WHILE CHECKING, and the label changes rather than the
                  control disappearing. A control that unmounts under a pointer
                  or a keyboard takes focus to `<body>` with it — the defect
                  this codebase has now fixed twice on the auth surface. Keeping
                  the same button, disabled, keeps the focus ring where it was.
                */}
                {/*
                  `aria-disabled`, NOT `disabled`. Measured in Chromium: the
                  browser blurs a focused control the moment it becomes
                  disabled, so focus reverted to `<body>` and a keyboard visitor
                  who pressed Enter lost their place — the identical defect this
                  codebase already fixed twice on the auth surface, reintroduced
                  here by the obvious fix for double activation.

                  `aria-disabled` announces the state without removing the
                  element from the tab order, so the focus ring stays exactly
                  where the person put it. The duplicate is then prevented where
                  it must be anyway: in the handler. `disabled` only ever
                  stopped a pointer click — Enter, Space and assistive
                  technology all still reach `onClick`.

                  `is-off` keeps it from looking pressable while it is not.
                */}
                <button
                  type="button"
                  className={`b-btn v-secondary s-sm${checking ? " is-off" : ""}`}
                  data-testid="review-uncertain-check"
                  aria-disabled={checking || undefined}
                  aria-busy={checking || undefined}
                  onClick={() => {
                    // REDUNDANT, AND KEPT KNOWINGLY. `useBooking.checkResult`
                    // opens with a synchronous `sending` latch, which is what
                    // actually prevents the duplicate — measured: deleting
                    // THIS line fails nothing, deleting the latch fails the
                    // suite. It stays as defence for a future caller wired to
                    // something other than that hook, and is recorded as
                    // untested rather than presented as a second layer.
                    if (checking) return;
                    onCheckResult?.();
                  }}
                >
                  {checking ? <span className="b2-spin" aria-hidden="true" /> : null}
                  {checking
                    ? t("flow.review.uncertain.busy")
                    : t("flow.review.uncertain.action")}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {settings ? (
          <div className="b-notice t-warning" role="alert" data-testid="review-settings">
            <div className="b-notice-b">
              <b>{t("flow.review.settings.title", { business: profile.businessName })}</b>
              <p className="b-p">{t("flow.review.settings.body")}</p>
              <div className="b-notice-a">
                <Link className="b-btn v-secondary s-sm" to={backToTime} onClick={() => requestStepFocus()}>
                  {t("flow.review.settings.action")}
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        {accountRequired ? (
          <div className="b-notice t-danger" role="alert" data-testid="review-account-required">
            <div className="b-notice-b">
              <b>{t("flow.review.accountRequired.title", { business: profile.businessName })}</b>
              <p className="b-p">
                {t("flow.review.accountRequired.body", { business: profile.businessName })}
              </p>
              {/* rc.13 (the Founder's dead-end finding): the notice says to
                  sign in, so its action is the existing "Sign in" (Web
                  Director, Q3). The selection rides along in the query. Only
                  a guest can be told an account is required (Backend's guest
                  check), and /signin is a route for guests only. */}
              {verifiedGuest ? (
                <p className="b-p">
                  <Link className="b2-text" to={`/${slug}/book/signin${search}`} data-testid="review-account-required-signin">
                    {t("flow.signin.submit")}
                  </Link>
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {alreadyBooked ? (
          <div className="b-notice t-info" role="status" data-testid="review-already-booked">
            <div className="b-notice-b">
              <b>{t("flow.review.alreadyBooked.title")}</b>
              {t("flow.review.alreadyBooked.body") ? <p className="b-p">
                {t("flow.review.alreadyBooked.body", { business: profile.businessName })}
              </p> : null}
            </div>
          </div>
        ) : null}

        {failed ? (
          <div className="b-notice t-danger" role="alert" data-testid="review-failed">
            <div className="b-notice-b">
              <b>{t("flow.review.failed.title")}</b>
              {/* The body branches on whether the hold SURVIVED the failure.
                  "Your time is still held" is a promise, and a send that failed
                  after the hold lapsed cannot keep it.

                  Keyed on `heldLabel` — the SAME expression that decides whether
                  the countdown renders — and not on `secondsLeft`. The two used
                  to be separate conditions that happened to agree, until
                  `incomplete` joined `blocked` and pulled the pill out from under
                  a sentence still promising a live hold. Sharing one expression
                  makes that class of drift impossible rather than merely fixed. */}
              <p className="b-p">
                {heldLabel !== null
                  ? t("flow.review.failed.body")
                  : t("flow.review.failed.bodyNoHold")}
              </p>
              <div className="b-notice-a">
                {/* Both this and the footer button call `onConfirm`, so they
                    have to agree about when that call is allowed — INCLUDING
                    how they say no.

                    A COMPONENT-LEVEL CONTRACT, NOT A MEASURED USER PATH. In the
                    composed app `failed` and `sending` cannot coexist: the
                    parent derives the failure from `status === "failed"`, so
                    this control never renders while busy. It matches Confirm
                    anyway, because this component is rendered directly by tests
                    and by the gallery and should not carry two different
                    answers to the same question.

                    The focus drop on THIS path is a different thing entirely —
                    the notice unmounts on dispatch — and it is handled by the
                    effect above, not here. An earlier version of this comment
                    claimed a measured defect that cannot occur in the shape
                    described; review caught the overclaim. */}
                <button
                  type="button"
                  className={`b-btn v-secondary s-sm${blocked || termsUndecided || sending ? " is-off" : ""}`}
                  data-testid="review-retry"
                  disabled={blocked || termsUndecided}
                  aria-disabled={sending || undefined}
                  aria-busy={sending || undefined}
                  onClick={() => {
                    if (sending || blocked || termsUndecided) return;
                    onConfirm();
                  }}
                >
                  {t("flow.review.failed.action")}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* THE CHECK WAS REFUSED ON THE TERMS (audit WEB-TOS-03). The same,
            Product-approved may-have-gone-through words as `uncertain`,
            and no new ones: the booking may exist, and nothing here may say
            otherwise. No Check button (it would resend the refused Terms);
            the Terms control below and Confirm are the way on, under the
            same key. With no live hold, another time, and never a claim that
            nothing was booked. */}
        {uncertainTerms ? (
          <div className="b-notice t-warning" role="status" data-testid="review-uncertain-terms">
            <div className="b-notice-b">
              <b>{t("flow.review.uncertain.title")}</b>
              <p className="b-p">{t("flow.review.uncertain.body")}</p>
              {uncertainNoHold ? (
                <div className="b-notice-a">
                  <Link className="b-btn v-secondary s-sm" to={backToTime} onClick={() => requestStepFocus()}>
                    {t("flow.review.nohold.action")}
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* THE TERMS CHANGED UNDER THE PERSON (TERMS_STALE, or
            TERMS_ACCEPTANCE_REQUIRED after a publication). No action here:
            the re-read Terms and their unticked box are below, and Confirm
            is the action once they agree. Nothing is re-sent for them. */}
        {termsChanged ? (
          <div className="b-notice t-warning" role="alert" data-testid="review-terms-changed">
            <div className="b-notice-b">
              <b>{t("terms.changed.title")}</b>
              <p className="b-p">{t("terms.changed.body")}</p>
            </div>
          </div>
        ) : null}

        {/* PAYMENT-MODE REFUSAL. No retry: the server refused this professional
            for this kind of booking, before anything was written, and it will
            refuse again. The existing title only — Product's wording for this
            case is pending, and no body is better than one that is false. */}
        {paymentUnavailable ? (
          <div className="b-notice t-danger" role="alert" data-testid="review-payment-unavailable">
            <div className="b-notice-b">
              <b>{t("flow.review.failed.title")}</b>
            </div>
          </div>
        ) : null}

        {/* No action button, on purpose. Every other notice offers a way
            forward; this one cannot, because there is nothing on this device
            that reconnects it. Offering a retry would imply the app is trying. */}
        {offline ? (
          <div className="b-notice t-warning" role="alert" data-testid="review-offline">
            <div className="b-notice-b">
              <p className="b-p">{t("flow.review.offline")}</p>
            </div>
          </div>
        ) : null}

        {(
          <div className="b3-review-facts" data-testid="review-rows">
            <section className="b3-review-section">
              <div className="b-between"><h2>{t("guest.review.appointment")}</h2>
                <Link to={`/${slug}/book/time${search}`} onClick={() => requestStepFocus()}>{t("guest.review.changeTime")}</Link></div>
              {startTime ? <p className="b3-review-date">{fmt.date(startTime, timeZone)}</p> : null}
              {startTime && endTime ? <p className="b3-review-time">{fmt.time(startTime, timeZone)} – {fmt.time(endTime, timeZone)}</p> : null}
              <p className="b-meta">{t("flow.time.zoneNote", { business: profile.businessName })}</p>
              <div className="b-between"><div><h3>{service?.name}</h3><p className="b-meta">{service ? fmt.duration(service.durationMinutes) : null}</p></div>
                <Link to={`/${slug}/book/service${search}`} onClick={() => requestStepFocus()}>{t("guest.review.changeService")}</Link></div>
              <p>{profile.businessName}</p>{profile.addressDisplay ? <p className="b-meta">{profile.addressDisplay}</p> : null}
            </section>
            <section className="b3-review-section">
              <div className="b-between"><h2>{t("guest.review.contact")}</h2>
                <Link to={`/${slug}/book/details${search}`} onClick={() => requestStepFocus()}>{t("guest.review.editDetails")}</Link></div>
              <p><span>{client.name}</span><br /><span>{client.email}</span></p>
            </section>
          </div>
        )}

        {/* The appointment happens where the professional is, and this is the
            last screen before it is booked. Repeated from StepTime rather than
            assumed remembered. */}

        {/* Lura takes no payment for this booking. Stated as its own card, in
            the same place the total appears, because "confirm" on a screen
            showing a price reads as "pay" unless something says otherwise. */}
        <div className="b-card" data-testid="review-pay">
          <div className="b-between">
            <span className="b-h3">{t("flow.review.pay.title")}</span>
            {/* Larger than a service-row price, per the design: this is the
                total, and it is the number someone checks before committing. */}
            {service ? (
              <span className="b-money" style={{ fontSize: "calc(19px*var(--bs))" }}>
                {fmt.money(service.priceInCents)}
              </span>
            ) : null}
          </div>
          {t("flow.review.pay.body") ? <p className="b-meta">
            {t("flow.review.pay.body", { business: profile.businessName })}
          </p> : null}
        </div>

        {/* THE POLICY IS THE SERVER'S, HEADLINE AND ALL.
            `cancellationPolicyDisplay` is the contract's only approved source
            for cancellation copy on this screen, so both lines come from it
            verbatim. Our old "Cancelling" label went with the placeholder: the
            server now supplies the heading, and keeping ours above it would put
            a second author on the one card whose words a client quotes back.

            Missing or blank policy copy blocks sending on both paths; no local
            cancellation rules substitute for Backend copy. */}
        {policyShown && cancellationPolicyDisplay ? (
          <PolicyCard policy={cancellationPolicyDisplay} testId="review-policy" cardRef={policyCard} />
        ) : null}

        {/* The Terms: last, as in the comp (4.3 / 6.1), directly above the
            action they gate. Not while a result is uncertain: the Check
            resends the frozen request, Terms included, and a box to tick
            would suggest otherwise. */}
        {uncertain ? null : (
          <TermsAgreement
            consent={terms}
            unavailableKey="terms.unavailable.booking"
            disabled={sending}
            keepPage={uncertainTerms}
          />
        )}
        {policyUnavailable ? <p className="b-notice t-warning" role="alert" data-testid="review-policy-unavailable">{t("guest.review.policyUnavailable")}</p> : null}
        {heldLabel !== null ? <FinishBy expiresAt={holdExpiresAt}
          timeZone={profile.timezone} now={deadlineNow} testId="review-finish-by" /> : null}
        <StepMeta />
      </div>

      <StepFoot>
        {/* Disabled while SENDING as well as while blocked. The design disables
            only on `blocked`, which leaves the button live under a "Submitting…"
            label — one impatient double-tap and the client has asked for the
            appointment twice. A duplicate booking is the expensive mistake on
            this screen, so this deviates from the design deliberately. */}
        {/* `is-off` tracks `disabled`, which includes `sending` — a button that
            cannot be pressed must not look pressable. Without the class it
            falls back to the User-Agent's disabled grey rather than the kit's. */}
        {/*
          NOT RENDERED AT ALL WHILE THE RESULT IS UNCERTAIN.

          Disabling it is not enough. The request may already have committed,
          and the only safe way to ask about it is the frozen replay in the
          notice above — so Confirm must not be a SECOND path to a send, not
          even a greyed one. A disabled "Submit booking" sitting under "Check
          booking result" also reads as the real action merely being
          unavailable, which invites someone to wait for it to come back rather
          than use the control that works.

          Removed rather than disabled, and this is the one state where that is
          right: everywhere else the button stays put precisely so focus is not
          dropped. Here the notice owns the action and owns the focus.
        */}
        {/*
          SENDING USES `aria-disabled`; BLOCKED USES THE REAL ATTRIBUTE.
          ────────────────────────────────────────────────────────────────────
          Both were `disabled` until QE measured the cost in both engines: the
          browser BLURS a focused control the moment it becomes disabled, so a
          visitor who pressed Enter on Confirm was returned to `<body>` for the
          whole of a slow submission. That is the identical defect this file
          already fixed twice — on the check control, and on entry to the
          uncertain notice — reintroduced on the primary action of the flow.

          The distinction is what the state MEANS:

            sending  - the control is BUSY, not gone. It is still the thing the
                       visitor is operating, so it keeps focus and announces
                       itself with `aria-disabled` + `aria-busy`. Removing it
                       from the accessibility tree mid-submission would take the
                       person's place away at the one moment they are waiting.
            blocked  - the action is genuinely UNAVAILABLE: no hold, the slot is
                       gone, the settings changed. Native `disabled` is correct
                       there, and Product's ordering means the visitor did not
                       just press it — a notice above explains why.

          DUPLICATE SUBMISSION IS UNCHANGED, and it never rested on `disabled`
          anyway: that attribute only ever stopped a pointer click. Enter, Space
          and assistive technology all reach `onClick` regardless. The guards
          that do the work are `useBooking.commit`'s synchronous `sending.current`
          latch and `Book`'s `hold.state.status !== "held"` check, both still in
          place, both still proven by the two-synchronous-activations tests.
          `is-off` keeps a busy control from looking pressable.
        */}
        {/* ABOVE THE BUTTON, ON SCREEN WITH IT (Legal/Product, 2026-10-15;
            placement by the Web Director, rc.10). Top to bottom: the approval
            line, the agreement, Google's reCAPTCHA disclosure, the button; all
            gone with the action while the result is uncertain. The approval
            line always, because Review cannot know whether this professional
            approves requests (booking-v1 carries no mode before the outcome).
            The agreement only when a policy is shown: it names the policy
            "shown above". One tight group, so the pinned bar keeps to half of
            a 320x481 screen in Spanish. Never clamped: a bar too tall for a
            short screen unpins (v3.css, 30em) rather than cutting the
            sentence. */}
        {uncertain ? null : (
          <div className="b3-action-notes">
            {/* Why the last press sent nothing (rc.10), announced politely
                and part of the description of the button while shown. */}
            {policyNote ? (
              <p className="b3-action-note b3-action-stop" id={`${actionNoteId}-stop`} role="status" data-testid="review-policy-stop">
                {t(policyNote === "changed" ? "flow.review.policyChanged" : "guest.review.policyUnavailable")}
              </p>
            ) : null}
            <p className="b3-action-note" id={`${actionNoteId}-approval`} data-testid="review-approval-note">{t("flow.review.approvalNote")}</p>
            {policyShown ? (
              <p className="b3-action-note" id={`${actionNoteId}-consent`} data-testid="review-policy-consent">
                {t(verifiedGuest ? "guest.review.policyConsent" : "flow.review.policyConsent")}
              </p>
            ) : null}
            <ActionRecaptchaNotice locale={locale} testId="review-recaptcha" />
          </div>
        )}
        {uncertain ? null : (
          <button
            type="button"
            className={`b-btn v-primary is-full${blocked || termsUndecided || sending ? " is-off" : ""}`}
            data-testid="step-review-confirm"
            aria-describedby={[policyNote ? `${actionNoteId}-stop` : null, `${actionNoteId}-approval`, policyShown ? `${actionNoteId}-consent` : null].filter(Boolean).join(" ")}
            ref={confirmButton}
            // Unticked, loading or unreadable Terms: nothing may be sent, and
            // the box right above says why.
            disabled={blocked || termsUndecided}
            aria-disabled={sending || undefined}
            aria-busy={sending || undefined}
            onClick={() => {
              // Redundant against the hook's latch and kept knowingly, because
              // this button is now reachable while busy.
              if (sending || blocked || termsUndecided) return;
              onConfirm();
            }}
          >
            {/* Offline keeps the normal label, disabled; the notice above says
                why (Web Director, rc.10 Q1). */}
            {sending ? (primaryLabels?.busy ?? t("flow.review.confirming")) : (primaryLabels?.idle ?? t("flow.review.confirm"))}
          </button>
        )}
      </StepFoot>
    </FlowFrame>
  );
}

export default StepReview;
